import { env } from "../constants"

/* i18n */
import { translate } from "../translates"

/* Redux store — to read the active language */
import { store } from "../store"

/* Fallback image — shown when organization has no photo or image fails to load */
export const FALLBACK_IMG = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='300'><rect width='400' height='300' fill='#eef2f7'/><g fill='none' stroke='#c0c8d4' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><rect x='158' y='117' width='84' height='66' rx='8'/><circle cx='179' cy='140' r='7'/><path d='M162 179 L191 152 L210 168 L226 157 L238 179'/></g></svg>`
)

export const onImgError = (event) => {
    if (event.target.dataset.fb) return
    event.target.dataset.fb = '1'
    event.target.src = FALLBACK_IMG
}

/* Transparent background so the avatar circle's own colour shows through (dark in header/profile) */
export const FALLBACK_AVATAR = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='76' height='76' viewBox='0 0 76 76'><g transform='translate(20 20) scale(1.5)' fill='none' stroke='#9ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2'/><path d='M12 11a4 4 0 100-8 4 4 0 000 8z'/></g></svg>`
)

export const onAvatarError = (event) => {
    if (event.target.dataset.fb) return
    event.target.dataset.fb = '1'
    event.target.src = FALLBACK_AVATAR
}

/* Build an absolute media url from a stored path */
export const media = path => {
    if (!path) {
        return ""
    }
    if (path.startsWith("http")) {
        return path
    }
    return `${env.mediapoint}${path}`
}

/* Format a distance in metres as a short human string */
export const distance = meters => {
    if (meters == null) {
        return ""
    }
    if (meters < 1000) {
        return `${Math.round(meters)} м`
    }
    return `${(meters / 1000).toFixed(1)} км`
}

/* True when the service has a valid sale price below its regular price */
export const hasDiscount = service => service && service.discount != null && Number(service.discount) > 0 && Number(service.discount) < Number(service.price)

/* Number the client actually pays — the discounted price when on sale, otherwise the regular price */
export const effectivePrice = service => hasDiscount(service) ? Number(service.discount) : (Number(service.price) || 0)

/* Format a service price by its kind: fixed | from | range.
   A zero / missing price means the master hasn't set it — show "По запросу" instead of "0 ₸".
   When the service is on sale, the discounted price is shown as a plain fixed amount */
export const price = service => {
    const value = Number(service.price) || 0
    if (!value) {
        return translate("По запросу")
    }
    const format = amount => `${Number(amount).toLocaleString("ru-RU")} ₸`
    if (hasDiscount(service)) {
        return format(Number(service.discount))
    }
    if (service.priceKind === "range" && service.priceTo) {
        return `${format(value)} – ${format(service.priceTo)}`
    }
    if (service.priceKind === "from") {
        /* Template keeps the word order correct per language ("от 5 000 ₸" vs "5 000 ₸ бастап") */
        return translate("от {price}").replace("{price}", format(value))
    }
    return format(value)
}

/* Plain tenge formatter */
export const money = value => `${Number(value || 0).toLocaleString("ru-RU")} ₸`

/* Russian plural — picks the correct word form for a count.
   forms = [one, few, many]:
   plural(1, ["отзыв", "отзыва", "отзывов"]) → "отзыв"
   plural(3, ["отзыв", "отзыва", "отзывов"]) → "отзыва"
   plural(5, ["отзыв", "отзыва", "отзывов"]) → "отзывов" */
export const plural = (count, forms) => {
    /* Kazakh does not inflect a counted noun — always use the base (singular) form, translated */
    if (store.getState().lang === "kk") {
        return translate(forms[0])
    }
    const number = Math.abs(Number(count) || 0)
    const lastTwo = number % 100
    const last = number % 10
    if (lastTwo > 10 && lastTwo < 20) {
        return forms[2]
    }
    if (last > 1 && last < 5) {
        return forms[1]
    }
    if (last === 1) {
        return forms[0]
    }
    return forms[2]
}

/* Human-friendly review date — "24 июня 2026". Falls back to the raw value if unparseable */
const MONTHS_GENITIVE = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"]
export const reviewDate = value => {
    if (!value) {
        return ""
    }
    const date = new Date(value)
    if (isNaN(date.getTime())) {
        return String(value)
    }
    return `${date.getDate()} ${translate(MONTHS_GENITIVE[date.getMonth()])} ${date.getFullYear()}`
}

/* Duration in minutes → "30 мин", "1 час", "1 час 15 мин", "2 часа" */
export const duration = value => {
    const total = Number(value) || 0
    if (total <= 0) {
        return ""
    }
    const hours = Math.floor(total / 60)
    const minutes = total % 60
    const parts = []
    if (hours > 0) {
        parts.push(`${hours} ${plural(hours, [translate("час"), translate("часа"), translate("часов")])}`)
    }
    if (minutes > 0) {
        parts.push(`${minutes} мин`)
    }
    return parts.join(" ")
}

/* Time only — "16:30". Empty if unparseable */
export const time = value => {
    if (!value) {
        return ""
    }
    const date = new Date(value)
    if (isNaN(date.getTime())) {
        return ""
    }
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`
}

/* Date and time — "13:00, 27 мая 2026". Empty if unparseable */
export const dateTime = value => {
    if (!value) {
        return ""
    }
    const date = new Date(value)
    if (isNaN(date.getTime())) {
        return ""
    }
    return `${time(value)}, ${date.getDate()} ${translate(MONTHS_GENITIVE[date.getMonth()])} ${date.getFullYear()}`
}

/* Short visit date — "11 июн 2026" (uppercased by CSS). Empty if unparseable */
const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"]
export const shortDate = value => {
    if (!value) {
        return ""
    }
    const date = new Date(value)
    if (isNaN(date.getTime())) {
        return ""
    }
    return `${date.getDate()} ${translate(MONTHS_SHORT[date.getMonth()])} ${date.getFullYear()}`
}

/* Format phone digits into the 18-char mask +7 (xxx) xxx-xx-xx */
export const phone = raw => {
    let digits = String(raw || "").replace(/\D/g, "")
    if (digits.startsWith("8")) {
        digits = `7${digits.slice(1)}`
    }
    if (digits.startsWith("7")) {
        digits = digits.slice(1)
    }
    digits = digits.slice(0, 10)
    let result = "+7"
    if (digits.length) {
        result += ` (${digits.slice(0, 3)}`
    }
    if (digits.length > 3) {
        result += `) ${digits.slice(3, 6)}`
    }
    if (digits.length > 6) {
        result += `-${digits.slice(6, 8)}`
    }
    if (digits.length > 8) {
        result += `-${digits.slice(8, 10)}`
    }
    return result
}
